/**
 * useChannelsSocket — escuta eventos de canais em tempo real.
 * - `channel:account-updated`: atualiza accountInfo + identifier do canal no cache.
 * - `channel:disconnected`: atualiza status + disconnectInfo do canal no cache.
 *
 * Usa string literal no socket.on pois Vite pre-bundle pode cachear versão antiga
 * do SOCKET_EVENTS vindo de @troia-v3/types (bug conhecido).
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSocket } from '@/contexts/SocketContext';
import type { ChannelAccountUpdatedEvent, ChannelDisconnectedEvent } from '@troia-v3/types';

export const useChannelsSocket = (enabled: boolean = true): void => {
  const { socket } = useSocket();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!socket || !enabled) return;

    const handleAccountUpdated = (data: ChannelAccountUpdatedEvent) => {
      queryClient.setQueriesData(
        { queryKey: ['channels'], exact: false },
        (oldData: any) => {
          if (!oldData) return oldData;

          // Cache pode ser:
          // - array direto: Channel[]
          // - paginado: { items: Channel[], total, ... }
          const updateChannel = (c: any) =>
            c && c.id === data.channelId
              ? { ...c, identifier: data.identifier, accountInfo: { ...(c.accountInfo ?? {}), ...data.accountInfo } }
              : c;

          if (Array.isArray(oldData)) {
            return oldData.map(updateChannel);
          }
          if (oldData.items && Array.isArray(oldData.items)) {
            return { ...oldData, items: oldData.items.map(updateChannel) };
          }
          return oldData;
        },
      );

      // Invalida também queries ['channel', id] para detalhe individual
      queryClient.invalidateQueries({ queryKey: ['channels', data.channelId] });
    };

    const handleDisconnected = (data: ChannelDisconnectedEvent) => {
      queryClient.setQueriesData(
        { queryKey: ['channels'], exact: false },
        (oldData: unknown) => {
          if (!oldData) return oldData;

          const updateChannel = (c: Record<string, unknown>) =>
            c && c.id === data.channelId
              ? { ...c, status: data.status, disconnectInfo: data.disconnectInfo }
              : c;

          if (Array.isArray(oldData)) {
            return (oldData as Record<string, unknown>[]).map(updateChannel);
          }
          const paged = oldData as { items?: Record<string, unknown>[] };
          if (paged.items && Array.isArray(paged.items)) {
            return { ...paged, items: paged.items.map(updateChannel) };
          }
          return oldData;
        },
      );

      // Invalida também queries ['channels', id] para detalhe individual
      queryClient.invalidateQueries({ queryKey: ['channels', data.channelId] });
    };

    socket.on('channel:account-updated', handleAccountUpdated);
    socket.on('channel:disconnected', handleDisconnected);

    return () => {
      socket.off('channel:account-updated', handleAccountUpdated);
      socket.off('channel:disconnected', handleDisconnected);
    };
  }, [socket, enabled, queryClient]);
};
